import { toast } from "react-toastify";

import type { DiscardAgenticLogicRuleDraftResponseBodyType } from "@intelligems/ig-types/functions/agentic-logic/rules/discard-draft";
import { getMutationErrorMessage } from "@src/queryClient";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import { agenticLogicKeys } from "../queryKeys";

/**
 * Put the rule's draft, settings and template configuration back to its live
 * version. The response is the reset rule and draft, so both are seeded into
 * the cache rather than refetched: an editor whose buffer matches its saved
 * draft follows the seeded draft in the same paint.
 */
export const useDiscardAgenticLogicRuleDraft = () => {
  const queryClient = useQueryClient();

  return useMutation<
    DiscardAgenticLogicRuleDraftResponseBodyType,
    Error,
    string
  >({
    mutationFn: async (ruleId) => {
      const resp = await api.post<
        DiscardAgenticLogicRuleDraftResponseBodyType,
        Record<string, never>
      >(`/v2/agentic-logic/rules/${ruleId}/discard-draft`, {});
      return resp.data;
    },
    onSuccess: async (data, ruleId) => {
      queryClient.setQueryData(
        agenticLogicKeys.rule(ruleId),
        (current: object | undefined) =>
          current ? { ...current, rule: data.rule } : current
      );
      if (data.draft !== null) {
        queryClient.setQueryData(agenticLogicKeys.draft(ruleId), {
          draft: data.draft,
        });
      }
      await queryClient.invalidateQueries({
        queryKey: agenticLogicKeys.rules(),
      });
    },
    onError: (error) => {
      const detail = getMutationErrorMessage(error);
      toast.error(
        `We could not discard your changes${detail ? `. ${detail}` : ""}`
      );
    },
  });
};
